import styles from "./ModelJobsPanel.module.scss";
import { ReactNode } from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import classNames from "classnames";
import { format } from "timeago.js";
import { MLTaskStatus } from "../../../../vl-api/vlSchemas";
import {
  getErrorText,
  KIND_TEXT,
  ModelJob,
  parseUtcDate,
  RESULT_ACTION,
  toPercent,
} from "./modelJobsUtils";

const STATUS_TEXT: Record<MLTaskStatus, string> = {
  INIT: "starting",
  IN_PROGRESS: "",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "aborted",
};

const STATUS_ICON: Record<MLTaskStatus, ReactNode> = {
  INIT: <ClockCircleOutlined />,
  IN_PROGRESS: <LoadingOutlined />,
  COMPLETED: <CheckCircleOutlined />,
  FAILED: <CloseCircleOutlined />,
  CANCELLED: <MinusCircleOutlined />,
};

export type ModelJobsPanelProps = {
  runningJobs: ModelJob[];
  finishedJobs: ModelJob[];
  seenIds: string[];
  canManageJobs: boolean;
  // Why a new job can't be started right now, if it can't
  newJobBlockedReason: string | null;
  onNewJob: () => void;
  onAbort: (job: ModelJob) => void;
  onViewModel: (job: ModelJob) => void;
  onViewError: (job: ModelJob) => void;
};

const getHeadline = (job: ModelJob) =>
  [KIND_TEXT[job.kind], STATUS_TEXT[job.status]].filter(Boolean).join(" ");

const Meta = ({ job, time }: { job: ModelJob; time: string }) => (
  <div className={styles.supporting}>
    {job.modelName && (
      <>
        <span className={styles.modelName}>{job.modelName}</span>
        <span className={styles.separator}>·</span>
      </>
    )}
    <span className={styles.time}>{time}</span>
  </div>
);

export const ModelJobsPanel = ({
  runningJobs,
  finishedJobs,
  seenIds,
  canManageJobs,
  newJobBlockedReason,
  onNewJob,
  onAbort,
  onViewModel,
  onViewError,
}: ModelJobsPanelProps) => {
  const hasJobs = runningJobs.length > 0 || finishedJobs.length > 0;

  const renderRunningItem = (job: ModelJob) => (
    <div key={job.id} className={styles.item}>
      <span className={classNames(styles.leading, styles[job.status])}>
        {STATUS_ICON[job.status]}
      </span>
      <div className={styles.content}>
        <div className={styles.headlineRow}>
          <span className={styles.headline}>{getHeadline(job)}</span>
          {canManageJobs && job.status === "IN_PROGRESS" && (
            <button
              type="button"
              className={styles.textButton}
              onClick={() => onAbort(job)}
            >
              Abort
            </button>
          )}
        </div>
        <Meta
          job={job}
          time={`Started ${format(parseUtcDate(job.createdAt))}`}
        />
        {job.status === "IN_PROGRESS" && (
          <div className={styles.progressRow}>
            <div
              className={styles.progressTrack}
              role="progressbar"
              aria-valuenow={toPercent(job.progress)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className={styles.progressIndicator}
                style={{ width: `${toPercent(job.progress)}%` }}
              />
            </div>
            <span className={styles.trailingText}>
              {toPercent(job.progress)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const renderResultAction = (
    label: string,
    hint: string,
    onClick: () => void,
  ) => (
    <Tooltip
      title={hint}
      placement="top"
      mouseEnterDelay={0.3}
      overlayClassName={styles.hintTooltip}
    >
      <button type="button" className={styles.textButton} onClick={onClick}>
        {label}
        <RightOutlined className={styles.chevron} aria-hidden />
      </button>
    </Tooltip>
  );

  const renderFinishedItem = (job: ModelJob) => {
    // Aborts are the user's own action, so they never count as unread
    const isUnread = job.status !== "CANCELLED" && !seenIds.includes(job.id);
    const errorText = job.status === "FAILED" ? getErrorText(job.error) : "";

    return (
      <div key={job.id} className={styles.item}>
        <span className={classNames(styles.leading, styles[job.status])}>
          {STATUS_ICON[job.status]}
        </span>
        <div className={styles.content}>
          <span
            className={classNames(styles.headline, {
              [styles.unread]: isUnread,
            })}
          >
            {getHeadline(job)}
          </span>
          {errorText && (
            <span className={styles.errorLine} title={errorText}>
              {errorText}
            </span>
          )}
          <Meta job={job} time={format(parseUtcDate(job.updatedAt))} />
        </div>
        <div className={styles.trailing}>
          {isUnread && <span className={styles.unreadDot} />}
          {job.status === "COMPLETED" &&
            renderResultAction(
              RESULT_ACTION[job.kind].label,
              RESULT_ACTION[job.kind].hint,
              () => onViewModel(job),
            )}
          {job.status === "FAILED" &&
            renderResultAction(
              "View error",
              "Full error details are in Task Manager",
              () => onViewError(job),
            )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <span className={styles.title}>Model Jobs</span>
          {canManageJobs && newJobBlockedReason && (
            <span className={styles.subtitle}>{newJobBlockedReason}</span>
          )}
        </div>
        {canManageJobs && (
          <button
            type="button"
            className={styles.tonalButton}
            disabled={!!newJobBlockedReason}
            onClick={onNewJob}
          >
            <PlusOutlined />
            New job
          </button>
        )}
      </div>
      {hasJobs ? (
        <div className={styles.body}>
          {runningJobs.length > 0 && (
            <section aria-label="Running">
              <div className={styles.subheader}>Running</div>
              {runningJobs.map(renderRunningItem)}
            </section>
          )}
          {finishedJobs.length > 0 && (
            <section aria-label="Recent">
              <div className={styles.subheader}>Recent</div>
              {finishedJobs.map(renderFinishedItem)}
            </section>
          )}
        </div>
      ) : (
        <div className={styles.empty}>
          <span className={styles.emptyTitle}>No model jobs yet</span>
          {canManageJobs && !newJobBlockedReason && (
            <span className={styles.supporting}>
              Train or validate a model on this dataset.
            </span>
          )}
        </div>
      )}
    </div>
  );
};
